import { FilterFilled } from "@ant-design/icons";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Table } from "antd";

const APP_TABLE_MOBILE_MAX_WIDTH = 600;

function useIsMobileTable() {
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== "undefined"
      ? window.matchMedia(`(max-width: ${APP_TABLE_MOBILE_MAX_WIDTH}px)`).matches
      : false,
  );

  useEffect(() => {
    const mq = window.matchMedia(
      `(max-width: ${APP_TABLE_MOBILE_MAX_WIDTH}px)`,
    );
    const onChange = (event) => setIsMobile(event.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  return isMobile;
}

function enhanceColumnsForMobile(columns) {
  if (!Array.isArray(columns)) return columns;

  return columns.map((col) => {
    if (Array.isArray(col.children) && col.children.length > 0) {
      return { ...col, children: enhanceColumnsForMobile(col.children) };
    }

    if (
      !Array.isArray(col.filters) ||
      col.filters.length === 0 ||
      col.filterIcon !== undefined
    ) {
      return col;
    }

    return {
      ...col,
      filterIcon: (filtered) => (
        <FilterFilled
          className={
            filtered
              ? "appTableFilterIcon appTableFilterIcon--active"
              : "appTableFilterIcon"
          }
        />
      ),
    };
  });
}

/** Hide «عدد الصفوف» when total pages ≤ this; show when pages exceed (matches product rule). */
export const APP_TABLE_SIZE_CHANGER_MIN_PAGES = 5;

/** Default when `pagination` prop is omitted (restore paged lists instead of one long scroll). */
export const appTableDefaultPagination = {
  pageSize: 10,
  showSizeChanger: true,
  pageSizeOptions: [
    "10",
    "20",
    "50",
    "100",
    "200",
    "300",
    "500",
    "700",
    "1000",
  ],
  showQuickJumper: false,
  showLessItems: true,
  position: ["bottomLeft"],
};

/**
 * Merges pagination with app defaults and toggles `showSizeChanger` from total record count:
 * visible only when `ceil(total / pageSize) > APP_TABLE_SIZE_CHANGER_MIN_PAGES`.
 *
 * `total` resolution: `pagination.total` → `meta.total` → `dataSource.length`
 */
export function resolveAppPagination(pagination, meta = {}) {
  if (pagination === false) return false;
  const { dataSource, total: metaTotal } = meta;
  const base = {
    ...appTableDefaultPagination,
    ...(pagination && typeof pagination === "object" ? pagination : {}),
  };
  const pageSize = Math.max(
    1,
    Number(base.pageSize || appTableDefaultPagination.pageSize)
  );
  let totalRecords =
    base.total !== undefined && base.total !== null
      ? Number(base.total)
      : metaTotal !== undefined && metaTotal !== null
        ? Number(metaTotal)
        : Array.isArray(dataSource)
          ? dataSource.length
          : 0;
  if (!Number.isFinite(totalRecords) || totalRecords < 0) totalRecords = 0;
  const pageCount = Math.max(1, Math.ceil(totalRecords / pageSize));
  return {
    ...base,
    showSizeChanger: pageCount > APP_TABLE_SIZE_CHANGER_MIN_PAGES,
  };
}

export default function AppTable({
  className,
  pagination,
  onChange,
  size,
  tableLayout,
  rowClassName,
  columns,
  ...rest
}) {
  const isMobile = useIsMobileTable();
  const paginationProp =
    pagination === false ? false : pagination ?? appTableDefaultPagination;

  const defaultPageSize = Math.max(
    1,
    Number(
      (typeof paginationProp === "object" && paginationProp?.pageSize) ||
        appTableDefaultPagination.pageSize
    )
  );

  const isPaginationControlled =
    pagination !== undefined &&
    pagination !== false &&
    typeof pagination === "object" &&
    (pagination.current !== undefined || pagination.pageSize !== undefined);

  const [pageCurrent, setPageCurrent] = useState(1);
  const [pageSize, setPageSize] = useState(defaultPageSize);

  useEffect(() => {
    if (!isPaginationControlled || typeof pagination !== "object") return;
    if (pagination.current !== undefined) {
      setPageCurrent(Math.max(1, Number(pagination.current) || 1));
    }
    if (pagination.pageSize !== undefined) {
      setPageSize(Math.max(1, Number(pagination.pageSize) || defaultPageSize));
    }
  }, [pagination, isPaginationControlled, defaultPageSize]);

  const resolvedPagination = useMemo(() => {
    const resolved = resolveAppPagination(paginationProp, {
      dataSource: rest.dataSource,
    });
    if (resolved === false) return false;

    const current =
      isPaginationControlled && pagination?.current !== undefined
        ? Math.max(1, Number(pagination.current) || 1)
        : pageCurrent;
    const size =
      isPaginationControlled && pagination?.pageSize !== undefined
        ? Math.max(1, Number(pagination.pageSize) || defaultPageSize)
        : pageSize;

    return {
      ...resolved,
      current,
      pageSize: size,
      onChange: (page, nextPageSize) => {
        if (!isPaginationControlled) {
          setPageCurrent(page);
          setPageSize(nextPageSize);
        }
        if (typeof paginationProp === "object" && paginationProp?.onChange) {
          paginationProp.onChange(page, nextPageSize);
        }
      },
    };
  }, [
    pagination,
    paginationProp,
    rest.dataSource,
    pageCurrent,
    pageSize,
    isPaginationControlled,
    defaultPageSize,
  ]);

  const handleTableChange = useCallback(
    (pag, filters, sorter, extra) => {
      if (pag && !isPaginationControlled) {
        setPageCurrent(Math.max(1, Number(pag.current) || 1));
        setPageSize(Math.max(1, Number(pag.pageSize) || defaultPageSize));
      }
      onChange?.(pag, filters, sorter, extra);
    },
    [onChange, isPaginationControlled, defaultPageSize]
  );

  const mergedRowClassName = (record, index) => {
    const zebra = index % 2 === 0 ? "appTableRowEven" : "appTableRowOdd";
    const extra =
      typeof rowClassName === "function"
        ? rowClassName(record, index)
        : rowClassName;
    return [zebra, extra].filter(Boolean).join(" ");
  };

  const resolvedColumns = useMemo(
    () => (isMobile ? enhanceColumnsForMobile(columns) : columns),
    [columns, isMobile],
  );

  const resolvedTableLayout =
    tableLayout ?? (isMobile && !rest.scroll?.x ? "auto" : "fixed");

  return (
    <Table
      className={["appTable", isMobile && "appTable--mobile", className]
        .filter(Boolean)
        .join(" ")}
      size={size ?? "middle"}
      pagination={resolvedPagination}
      tableLayout={resolvedTableLayout}
      rowClassName={mergedRowClassName}
      onChange={handleTableChange}
      columns={resolvedColumns}
      {...rest}
    />
  );
}
